import { useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useIdProject } from '@/lib/hooks'
import type {
  CompanyLogsQuery,
  CompanyLogsQueryVariables,
  UrlAuthTokenQuery,
  UrlAuthTokenQueryVariables,
} from '@/components/shared/logs-explorer/hooks/use-log-stream.types.gen'
import type { ResolvedLogFilter } from '@/components/shared/logs-explorer/types'
import type { Log } from '@/graphql.types'

const logStreamUrl =
  import.meta.env.VITE_LOG_STREAM_URL ??
  'https://mk-app-management-service-rest-dev.kinsta.services'

const URL_AUTH_TOKEN = gql`
  query UrlAuthToken($idCompany: String!) {
    company(id: $idCompany) {
      id
      urlAuthToken
    }
  }
`

const COMPANY_LOGS = gql`
  query CompanyLogs(
    $idCompany: String!
    $idProject: String
    $service: LogService
    $from: Date
    $to: Date
    $filterText: String
    $tags: JSON
    $attributes: JSON
    $filters: [MetricsFilterInput!]
  ) {
    company(id: $idCompany) {
      id
      logs(
        idProject: $idProject
        service: $service
        from: $from
        to: $to
        filterText: $filterText
        tags: $tags
        attributes: $attributes
        filters: $filters
      ) {
        id
        message
        service
        severity
        timestamp
        unixTimestamp
        attributes
        tags
        __typename
      }
      __typename
    }
  }
`

type UseLogStreamConfig = {
  filter: ResolvedLogFilter
  type?: 'fetch' | 'stream'
  isAutoStopEnabled?: boolean
  isStreaming: boolean
  setIsStreaming: (value: boolean) => void
  onLoading?: (isLoading: boolean) => void
}

type UseLogStreamReturn = {
  logs: Log[]
  isLoading: boolean
  isRefreshing: boolean
  refresh: () => void
  fetchLogs: (options: Partial<ResolvedLogFilter>) => Promise<Log[]>
  setLogs: React.Dispatch<React.SetStateAction<Log[]>>
}

export const useLogStream = ({
  filter,
  type = 'stream',
  isAutoStopEnabled = true,
  isStreaming,
  setIsStreaming,
}: UseLogStreamConfig): UseLogStreamReturn => {
  const { idCompany } = useIdCompany()
  const { idProject } = useIdProject()
  const client = useApolloClient()
  const eventSourceRef = useRef<EventSource | null>(null)
  const [logs, setLogs] = useState<Log[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const isHistoricalQuery = useMemo(() => {
    if (!filter.to) {
      return false
    }
    return filter.to.getTime() < Date.now() - 60000
  }, [filter.to])

  const effectiveType = isHistoricalQuery ? 'fetch' : type

  const fetchLogs = useCallback(
    async (options: Partial<ResolvedLogFilter>) => {
      try {
        const { data, error } = await client.query<
          CompanyLogsQuery,
          CompanyLogsQueryVariables
        >({
          query: COMPANY_LOGS,
          variables: {
            ...filter,
            ...options,
            idCompany,
            idProject,
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        })
        if (error) {
          console.error('Failed to fetch logs:', error.message)
          return []
        }
        return data?.company?.logs ?? []
      } catch (error) {
        console.error('Failed to fetch logs:', error)
        return []
      }
    },
    [client, filter, idCompany, idProject]
  )

  // Reset logs when filter or type changes in stream mode
  useEffect(() => {
    if (effectiveType === 'stream') {
      setLogs([])
      setIsStreaming(true)
    }
  }, [filter, effectiveType, setIsStreaming])

  // Main stream/fetch effect
  useEffect(() => {
    let isActive = true

    if (effectiveType === 'fetch') {
      setIsLoading(true)
      fetchLogs(filter).then((fetchedLogs) => {
        if (!isActive) {
          return
        }
        setLogs(fetchedLogs)
        setIsLoading(false)
      })
      return () => {
        isActive = false
      }
    }

    if (!idCompany || !isStreaming) {
      return
    }

    setIsLoading(true)

    const base64Filter = btoa(JSON.stringify(filter))

    let eventSource: EventSource
    const openStream = async () => {
      try {
        const { data, error: authError } = await client.query<
          UrlAuthTokenQuery,
          UrlAuthTokenQueryVariables
        >({
          query: URL_AUTH_TOKEN,
          variables: { idCompany },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        })

        if (authError) {
          console.error('Failed to get auth token:', authError.message)
          setIsLoading(false)
          return
        }

        if (!isActive) {
          return
        }

        eventSource = new EventSource(
          `${logStreamUrl}/logs/${idCompany}?filter=${base64Filter}&token=${data?.company?.urlAuthToken}${idProject ? `&idProject=${idProject}` : ''}`
        )
        eventSourceRef.current = eventSource

        eventSource.onopen = () => {
          if (!isActive) {
            return
          }
        }

        let counter = 0
        eventSource.onmessage = (event) => {
          if (!isActive) {
            return
          }
          if (counter === 0) {
            setIsLoading(false)
          }
          counter++
          try {
            setLogs((prevLogs) => [...prevLogs, ...JSON.parse(event.data)])
          } catch {
            console.error('Failed to parse log data')
          }
          if (counter > (isAutoStopEnabled ? 200 : Infinity)) {
            setIsStreaming(false)
            eventSource.close()
          }
        }

        eventSource.onerror = (error) => {
          if (!isActive) {
            return
          }
          console.error('Stream error:', error)
          setIsLoading(false)
          eventSource.close()
        }
      } catch (error) {
        console.error('Failed to open log stream:', error)
        setIsLoading(false)
      }
    }

    openStream()

    return () => {
      isActive = false
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [
    idProject,
    filter,
    isStreaming,
    effectiveType,
    refreshKey,
    fetchLogs,
    client,
    idCompany,
    isAutoStopEnabled,
    setIsStreaming,
  ])

  const refresh = useCallback(() => {
    setIsRefreshing(true)
    setLogs([])
    setIsStreaming(true)
    setRefreshKey((prev) => prev + 1)
    setTimeout(() => setIsRefreshing(false), 500)
  }, [setIsStreaming])

  return {
    logs,
    isLoading,
    isRefreshing,
    refresh,
    fetchLogs,
    setLogs,
  }
}
